<template>
  <div v-if="show" class="daily-report-popup-overlay" ref="dailyReportPopup">
    <div class="daily-report-popup">
      <div class="daily-report-popup-body">
        <div v-if="loading" class="daily-report-popup-message">Loading jobs...</div>
        <div v-else-if="errorMessage" class="daily-report-popup-message error">{{ errorMessage }}</div>
        <TableBox
          v-else
          title="Import Job Customs Status Report Jobs"
          :tableData="renderedJobs"
          :loading="loading"
          :dataType="jobDataType"
          :downloadName="downloadName"
          :hideColumns="hiddenColumns"
          class="daily-report-jobs-table"
        />
      </div>

      <div class="daily-report-popup-footer">
        <Dropdown
          placeholder="Criteria"
          :items="filterOptions"
          itemKey="title"
          class="w-100"
          :selectedObject.sync="selectedFilter"
          @selected="filterSelected"
        />

        <button @click="closePopup">Close</button>
      </div>
    </div>
  </div>
</template>

<script>
import TableBox from '@/components/v2testComponent/TableBox.vue';
import Dropdown from '@/components/v2testComponent/DropdownV2.vue';

export default {
  components: {
    TableBox,
    Dropdown,
  },
  props: {
    show: { type: Boolean, default: false },
    selectedItem: { type: Object, default: null },
    payload: { type: Object, default: () => ({}) },
    items: { type: Array, default: () => [] },
  },
  data() {
    return {
      jobs: [],
      renderedJobs: [],
      loading: false,
      errorMessage: '',
      selectedFilter: null,
    };
  },
  computed: {
    totalJobsFilter() {
      return {
        title: 'Total Jobs',
        value: '',
        percentage: null,
        category: '',
      };
    },
    filterOptions() {
      const options = [this.totalJobsFilter];
      this.items.forEach((item) => {
        if (!item?.title || item.title == this.totalJobsFilter.title) return;
        options.push({
          title: item.title,
          value: item.value,
          percentage: item.percentage,
          category: item.category || '',
        });
      });
      return options;
    },
    selectedItemTitle() {
      return this.selectedFilter?.title || 'Import Job Customs Status Report Jobs';
    },
    dateLabel() {
      const from = this.payload?.datefrom || 'Backend default';
      const to = this.payload?.dateto || 'Today';
      return `${from} to ${to}`;
    },
    downloadName() {
      return `${this.selectedItemTitle} ${this.dateLabel}`;
    },
    displayJobs() {
      return this.filteredNormalizedJobs.map((job, index) => ({
        '#': index + 1,
        ...job,
      }));
    },
    filteredNormalizedJobs() {
      if (!this.selectedFilter || this.selectedFilter.title == this.totalJobsFilter.title) return this.normalizedJobs;
      const hasFilterMetadata = this.normalizedJobs.some(
        (job) => job['Daily Report Title'] || job['Daily Report Category'],
      );

      return this.normalizedJobs.filter((job) => {
        if (!hasFilterMetadata) return this.matchesSelectedFilter(job);
        const rawTitle = job['Daily Report Title'] || '';
        const rawCategory = job['Daily Report Category'] || '';
        return rawTitle == this.selectedFilter.title || rawCategory == this.selectedFilter.category;
      });
    },
    normalizedJobs() {
      const jobsByNumber = {};
      this.jobs.forEach((job) => {
        const normalizedJob = this.normalizeJobRow(job);
        const jobNo = normalizedJob['Job No.'] || normalizedJob['Job No'] || normalizedJob.JobNo || '';
        const key = jobNo || JSON.stringify(normalizedJob);
        jobsByNumber[key] = {
          ...(jobsByNumber[key] || {}),
          ...normalizedJob,
        };
      });
      return Object.values(jobsByNumber);
    },
    jobDataType() {
      return {
        '#': 'number',
        'Job Date': 'date',
        ETA: 'date',
        '1253A - Paper (Cust.Form) To Operations For Submission': 'date',
        '0209 - Submit to Customs': 'date',
        '0210 - Paper Pass': 'date',
        '0221 - Customs Released': 'date',
        'Days (0209 -> 0221)': 'number',
      };
    },
    hiddenColumns() {
      return ['Daily Report Title', 'Daily Report Category'];
    },
  },
  methods: {
    closePopup() {
      this.$emit('update:show', false);
    },
    teleportPopup() {
      const popup = this.$refs.dailyReportPopup;
      const overlayRoot = this.$getOverlayRoot();
      if (!popup || popup.parentNode == overlayRoot) return;
      overlayRoot.appendChild(popup);
    },
    removePopup() {
      const popup = this.$refs.dailyReportPopup;
      if (popup && popup.parentNode == this.$getOverlayRoot()) {
        popup.parentNode.removeChild(popup);
      }
    },
    getFilterTitle() {
      if (!this.selectedFilter || this.selectedFilter.title == this.totalJobsFilter.title) return '';
      return this.selectedFilter.title || '';
    },
    getFilterCategory() {
      if (!this.selectedFilter || this.selectedFilter.title == this.totalJobsFilter.title) return '';
      return this.selectedFilter.category || '';
    },
    initializeSelectedFilter() {
      if (this.selectedItem?.title) {
        this.selectedFilter =
          this.filterOptions.find((option) => option.title == this.selectedItem.title) || this.totalJobsFilter;
      } else {
        this.selectedFilter = this.totalJobsFilter;
      }
    },
    async filterSelected(selectedFilter) {
      if (selectedFilter) this.selectedFilter = selectedFilter;
      if (this.show) await this.getJobs();
    },
    async updateRenderedJobs() {
      this.renderedJobs = [];
      await this.$nextTick();
      this.renderedJobs = this.displayJobs;
    },
    hasJobValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    matchesSelectedFilter(job) {
      const title = String(this.selectedFilter?.title || '').toLowerCase();
      const sentToOperations = this.hasJobValue(job['1253A - Paper (Cust.Form) To Operations For Submission']);
      const submitted = this.hasJobValue(job['0209 - Submit to Customs']);
      const paperPassed = this.hasJobValue(job['0210 - Paper Pass']);
      const released = this.hasJobValue(job['0221 - Customs Released']);

      if (title.includes('not sent to operations')) return !sentToOperations;
      if (title.includes('paper passed without submission')) return paperPassed && !submitted;
      if (title.includes('released without paper pass')) return released && !paperPassed;
      if (title.includes('not submitted to customs')) return !submitted;
      if (title.includes('not received') || title.includes('released date after paper')) return paperPassed && !released;
      if (title.includes('submitted to customs, paper passed & released')) {
        return submitted && paperPassed && released;
      }
      if (title.includes('submitted to customs & released')) return submitted && released;
      if (title.includes('paper passed & released')) return paperPassed && released;
      if (title.includes('paper passed')) return paperPassed;
      if (title.includes('submitted to customs')) return submitted;

      return true;
    },
    normalizeJobRow(job) {
      return {
        'Job Date': job['Job Date'] || job.jobDate || job.JobDate || '',
        'Job No.': job['Job No.'] || job['Job No'] || job.jobNo || job.JobNo || '',
        'Cargo Type': job['Cargo Type'] || job.cargoType || job.CargoType || '',
        ETA: job.ETA || job.eta || '',
        Customer: job.Customer || job.customer || '',
        'Daily Report Title': job.title || job.Title || job.analytic || job.Analytic || '',
        'Daily Report Category': job.category || job.Category || job.type || job.Type || '',
        '1253A - Paper (Cust.Form) To Operations For Submission':
          job['1253A - Paper (Cust.Form) To Operations For Submission'] ||
          job['Paper To Operations Date'] ||
          job.paperToOperationsDate ||
          job.PaperToOperationsDate ||
          '',
        '0209 - Submit to Customs':
          job['0209 - Submit to Customs'] ||
          job['Submitted to Customs Date'] ||
          job.submittedToCustomsDate ||
          job.SubmittedToCustomsDate ||
          '',
        '0210 - Paper Pass':
          job['0210 - Paper Pass'] || job['Paper Passed Date'] || job.paperPassedDate || job.PaperPassedDate || '',
        '0221 - Customs Released':
          job['0221 - Customs Released'] || job['Released Date'] || job.releasedDate || job.ReleasedDate || '',
        'Days (0209 -> 0221)':
          job['Days (0209 -> 0221)'] ||
          job.days0209To0221 ||
          job.Days0209To0221 ||
          this.getDaysBetween(
            job['0209 - Submit to Customs'] ||
              job['Submitted to Customs Date'] ||
              job.submittedToCustomsDate ||
              job.SubmittedToCustomsDate,
            job['0221 - Customs Released'] || job['Released Date'] || job.releasedDate || job.ReleasedDate,
          ),
      };
    },
    getDaysBetween(dateFrom, dateTo) {
      if (!dateFrom || !dateTo) return '';
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
      return Math.round((to - from) / (1000 * 60 * 60 * 24));
    },
    async getJobs() {
      this.loading = true;
      this.errorMessage = '';
      this.jobs = [];
      this.renderedJobs = [];

      if (!this.selectedFilter) this.initializeSelectedFilter();

      const result = await this.$apiCallV2(
        '/Dashboard/ImportDailyReportJobs',
        {
          ...this.payload,
          title: this.getFilterTitle(),
          category: this.getFilterCategory(),
        },
        'POST',
        {
          message: 'Import Job Customs Status Report Jobs API failed',
        },
      );

      if (result?.Results == '1') {
        this.errorMessage = result.ErrMsg || 'Unable to load jobs.';
      } else {
        this.jobs = result?.jobs || result?.Jobs || [];
        await this.updateRenderedJobs();
      }
      this.loading = false;
    },
  },
  watch: {
    show: {
      handler(value) {
        if (value) {
          this.initializeSelectedFilter();
          this.$nextTick(() => {
            this.teleportPopup();
          });
          this.getJobs();
        }
      },
      immediate: true,
    },
    selectedItem() {
      if (!this.show) return;
      this.initializeSelectedFilter();
      this.getJobs();
    },
  },
  beforeDestroy() {
    this.removePopup();
  },
};
</script>

<style lang="scss">
.daily-report-popup-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 99990;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Roboto Mono', monospace;
  font-size: 0.75rem;
}

.daily-report-popup {
  background-color: white;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  width: min(96vw, 78rem);
  height: min(86vh, 46rem);
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
}

.daily-report-popup-body {
  min-height: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
}

.daily-report-popup-message {
  text-align: center;
  padding: 2rem;
  color: #64748b;

  &.error {
    color: #9f1239;
  }
}

.daily-report-jobs-table {
  flex: 1 1 auto;
  min-height: 0;
}

.daily-report-popup-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-shrink: 0;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;

  button {
    min-width: 6rem;
  }
}
</style>
